import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axiosClient from "../axiosClient";
import "./middleSchoolPrep.css";
import wechatQr from "./images/svt-wechat-support.jpg";
import MiddleSchoolPrepStripeCheckout from "./middleSchoolPrepStripeCheckout";

const PROGRAM_OPTIONS = [
  {
    value: "5-Day Program",
    label: "5-Day Program",
    priceLabel: "$1,588 / month",
  },
  {
    value: "3-Day Program",
    label: "3-Day Program",
    priceLabel: "$1,299 / month",
  },
];

const ENGLISH_DOCUMENTS = [
  {
    label: "Admission Brochure",
    file: "SVT MS Prep AdmissionBrochure_0917.pdf",
  },
  {
    label: "Weekly Schedule",
    file: "SVT MS Prep Wkly Schedule.pdf",
  },
  {
    label: "ELA Reading & Writing Syllabus (16 weeks)",
    file: "SVT MSprep ELA  Reading&Writing_16Wk Syllabus.pdf",
  },
  {
    label: "Pre-Algebra / IMT1 Syllabus (16 weeks)",
    file: "SVT MSprep PreAlgebra_IMT1_16Wk_Syllabus.pdf",
  },
  {
    label: "Science Syllabus (16 weeks)",
    file: "SVT_Science_16Week_Syllabus_EN.pdf",
  },
];

const CHINESE_DOCUMENTS = [
  {
    label: "招生简章",
    file: "SVT初中预备班招生简章_0917.pdf",
  },
  {
    label: "每周课程安排",
    file: "SVT初中预备班每周课程安排.pdf",
  },
  {
    label: "阅读与理解大纲",
    file: "SVT初中预备班阅读&理解大纲.pdf",
  },
  {
    label: "数学 PreAlgebra / IMT1 大纲",
    file: "SVT初中预备班数学 PreAlgebra_IMT1大纲.pdf",
  },
  {
    label: "科学课程设置（16周）",
    file: "SVT16周科学课程设置.pdf",
  },
];

const WAIVER_PDF = "SVT MSprep Waiver免责条款_EN_CN.pdf";
const TPR_POLICY_PDF = "SVT_MSprep TPR policy预备班付费政策.pdf";

const pdfHref = (fileName) =>
  `./${encodeURIComponent(fileName).replace(/%2F/g, "/")}`;

const MiddleSchoolPrep = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [dialogMessage, setDialogMessage] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [paymentErrors, setPaymentErrors] = useState({});
  const [paymentForm, setPaymentForm] = useState({
    programOption: "5-Day Program",
    studentName: "",
  });
  const [stripeCheckout, setStripeCheckout] = useState(null);
  const [paymentLoadError, setPaymentLoadError] = useState("");

  useEffect(() => {
    if (searchParams.get("paid") === "1") {
      setDialogMessage(
        "Thank you for your payment. Your Middle School Prep enrollment payment was received. We will contact you with next steps."
      );
      setShowDialog(true);
      setSearchParams({});
    } else if (searchParams.get("cancelled") === "1") {
      setDialogMessage(
        "Payment was not completed. You can try again when you are ready."
      );
      setShowDialog(true);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handlePaymentFieldChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm((prev) => ({ ...prev, [name]: value }));
    setPaymentErrors((prev) => ({ ...prev, [name]: "" }));

    if (name === "programOption") {
      setStripeCheckout(null);
      setPaymentLoadError("");
    }
  };

  const closePaymentModal = () => {
    if (isPaying) {
      return;
    }
    setShowPaymentModal(false);
    setStripeCheckout(null);
    setPaymentErrors({});
    setPaymentLoadError("");
  };

  const loadPaymentQuote = async (programOption) => {
    if (!programOption || isPaying) {
      return;
    }

    setIsPaying(true);
    setPaymentLoadError("");

    try {
      const response = await axiosClient.get("/middleSchoolPrepRegistrations/quote", {
        params: { programOption },
      });

      if (response.data.publishableKey && response.data.amountCents) {
        setStripeCheckout({
          publishableKey: response.data.publishableKey,
          amountCents: response.data.amountCents,
          amountDisplay: response.data.amountDisplay,
          baseDisplay: response.data.baseDisplay,
          feeDisplay: response.data.feeDisplay,
          programOption,
        });
        return;
      }

      setPaymentLoadError("Unable to load the card payment form. Please try again.");
    } catch (error) {
      const response = error.response;
      setPaymentLoadError(
        response?.data?.message ||
          response?.data?.error ||
          "We could not load the card payment form. Please try again later."
      );
    } finally {
      setIsPaying(false);
    }
  };

  useEffect(() => {
    if (!showPaymentModal || !paymentForm.programOption) {
      return undefined;
    }

    if (stripeCheckout) {
      return undefined;
    }

    loadPaymentQuote(paymentForm.programOption);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPaymentModal, paymentForm.programOption, stripeCheckout]);

  const handlePaymentSuccess = () => {
    setStripeCheckout(null);
    setShowPaymentModal(false);
    setPaymentForm({
      programOption: "5-Day Program",
      studentName: "",
    });
    setDialogMessage(
      "Thank you for your payment. Your Middle School Prep enrollment payment was received. We will contact you with next steps."
    );
    setShowDialog(true);
  };

  const openPaymentModal = () => {
    if (!waiverAccepted) {
      return;
    }
    setShowPaymentModal(true);
  };

  return (
    <div className="middle-school-prep-page">
      <div className="middle-school-prep-container">
        <div className="middle-school-prep-main">
          <div className="middle-school-prep-intro">
            <div className="middle-school-prep-intro-col middle-school-prep-intro-en">
              <p className="middle-school-prep-intro-title">
                <strong>SVT Middle School Prep Program</strong>
              </p>
              <p>
                This program is designed for students entering or currently in
                grades 4 and 5, aiming to help them build a solid foundation and
                ensure a smooth transition to middle school. The academic
                standards align with the Bellevue School District&apos;s
                curriculum requirements for grades 4 and 5 while effectively
                bridging the gap to middle school coursework. The core curriculum
                includes English Language Arts (ELA, covering reading and
                writing), Pre-Algebra/Math, Science, a baking workshop, hands-on
                projects, &quot;Homework Studio&quot; (providing homework
                assistance), and support for personal development and planning.
              </p>
              <p>
                Academic courses follow a unified design framework:
              </p>
              <ul>
                <li>
                  <strong>Mathematics:</strong> SVT Middle School Prep Course
                  (aligned with district 5th-grade standards and bridging to the
                  IMT1 course)
                </li>
                <li>
                  <strong>English (ELA):</strong> Focused on the core areas of
                  reading and writing
                </li>
                <li>
                  <strong>Science:</strong> Combines introductory middle school
                  science content with a review of key 5th-grade concepts
                </li>
              </ul>
              <p>
                Value-added components include weekly or monthly communication
                regarding student social-emotional development and monthly
                consultations with parents on academic progression and planning.
              </p>
              <p>
                The weekly schedule runs from 2:50 PM to 6:00 PM on Mondays,
                Tuesdays, Thursdays, and Fridays, and from 12:35 PM to 6:00 PM on
                Wednesdays. The schedule comprises 75 minutes of academic
                instruction, a 45-minute homework assistance session, and a
                15-minute break; Wednesdays feature a baking workshop and other
                hands-on projects. Students may choose between two enrollment
                options: a full-week (5-day) program or a 3-day program. Early
                pickup is permitted.
              </p>
            </div>
            <div className="middle-school-prep-intro-col middle-school-prep-intro-zh">
              <p className="middle-school-prep-intro-title">
                <strong>SVT 初中预备班项目</strong>
              </p>
              <p>
                本项目主要面向即将升入或就读于四年级和五年级的学生，旨在帮助他们打下坚实基础并顺利过渡到初中阶段。学术标准既符合贝尔维尤学区（Bellevue
                School
                District）四年级和五年级的教学要求，又能有效衔接初中课程。核心课程包括：英语语言艺术（ELA，涵盖阅读与写作）、代数预科/数学、科学、烘焙工作坊、动手实践项目、“作业辅导室”（Homework
                Studio，提供作业辅导），以及个人发展与规划支持。
              </p>
              <p>学术课程遵循统一的设计框架：</p>
              <ul>
                <li>
                  <strong>数学：</strong>
                  SVT初中预备课程（对标学区五年级标准，并衔接IMT1课程）
                </li>
                <li>
                  <strong>英语（ELA）：</strong>
                  以阅读和写作两大核心板块为重点
                </li>
                <li>
                  <strong>科学：</strong>
                  结合初中科学入门内容与五年级关键概念复习
                </li>
              </ul>
              <p>
                增值项目包括：每周／月学生心智发展沟通、每月面向家长的升学规划咨询。
              </p>
              <p>
                每周日程安排为：周一、周二、周四和周五下午2:50至6:00，周三下午12:35至6:00。日程包含75分钟的学术课程、45分钟的作业辅导时段和15分钟的休息时间；周三特别安排了烘焙工作坊及其他动手实践项目。学生可选择两种注册方案：全周（5天）课程或3天课程。允许提前接回学生。
              </p>
            </div>
          </div>

          <div className="middle-school-prep-documents">
            <div className="middle-school-prep-documents-col">
              <h3>English Documents</h3>
              <ul>
                {ENGLISH_DOCUMENTS.map((doc) => (
                  <li key={doc.file}>
                    Download{" "}
                    <a
                      href={`./${encodeURIComponent(doc.file).replace(/%2F/g, "/")}`}
                      target="msprepdocs"
                      rel="noopener noreferrer"
                    >
                      {doc.label}
                    </a>{" "}
                    in PDF format.
                  </li>
                ))}
              </ul>
            </div>
            <div className="middle-school-prep-documents-col">
              <h3>中文资料</h3>
              <ul>
                {CHINESE_DOCUMENTS.map((doc) => (
                  <li key={doc.file}>
                    下载{" "}
                    <a
                      href={`./${encodeURIComponent(doc.file).replace(/%2F/g, "/")}`}
                      target="msprepdocs"
                      rel="noopener noreferrer"
                    >
                      {doc.label}
                    </a>{" "}
                    （PDF）
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="middle-school-prep-join-section">
            <label className="middle-school-prep-waiver">
              <span className="middle-school-prep-waiver-en">
                <input
                  type="checkbox"
                  checked={waiverAccepted}
                  onChange={(e) => setWaiverAccepted(e.target.checked)}
                />
                <span>
                  Please read our{" "}
                  <a
                    href={pdfHref(WAIVER_PDF)}
                    target="msprepdocs"
                    rel="noopener noreferrer"
                  >
                    disclaimer
                  </a>{" "}
                  and{" "}
                  <a
                    href={pdfHref(TPR_POLICY_PDF)}
                    target="msprepdocs"
                    rel="noopener noreferrer"
                  >
                    tuition payment policy
                  </a>{" "}
                  before pay.
                </span>
              </span>
              <span className="middle-school-prep-waiver-zh">
                请在付款前阅读
                <a
                  href={pdfHref(WAIVER_PDF)}
                  target="msprepdocs"
                  rel="noopener noreferrer"
                >
                  免责条款
                </a>
                和
                <a
                  href={pdfHref(TPR_POLICY_PDF)}
                  target="msprepdocs"
                  rel="noopener noreferrer"
                >
                  学费付费政策
                </a>
                。
              </span>
            </label>
            <button
              type="button"
              className="middle-school-prep-join-button"
              onClick={openPaymentModal}
              disabled={!waiverAccepted}
            >
              Pay 付款
            </button>
          </div>

          <aside className="middle-school-prep-contact-aside middle-school-prep-contact-centered">
            <h3>Contact Us</h3>
            <p className="middle-school-prep-phone">
              Phone:{" "}
              <a href="tel:+14254571965">(+1) 425-457-1965</a>
            </p>
            <p className="middle-school-prep-email">
              Email:{" "}
              <a href="mailto:contact@seattlevt.org">contact@seattlevt.org</a>
            </p>
            <p className="middle-school-prep-qr-caption">
              Scan to add SVT Support on WeChat
            </p>
            <img
              src={wechatQr}
              alt="Scan QR code to add SVT Support on WeChat"
              className="middle-school-prep-qr"
            />
          </aside>
        </div>
      </div>

      {showPaymentModal && (
        <div className="middle-school-prep-dialog-overlay">
          <div className="middle-school-prep-payment-modal">
            <button
              type="button"
              className="middle-school-prep-payment-close"
              onClick={closePaymentModal}
              aria-label="Close"
            >
              ×
            </button>
            <h3>Payment Instructions 付款指南</h3>

            <div className="middle-school-prep-pay-methods">
              <section className="middle-school-prep-pay-method">
                <h4>Zelle</h4>
                <p>4254571965</p>
              </section>

              <section className="middle-school-prep-pay-method">
                <h4>Check</h4>
                <p>Check payable to Seattle Voctech Education.</p>
              </section>

              <section className="middle-school-prep-pay-method">
                <h4>Credit Card</h4>
                <p>
                  Pay online by credit card with Stripe processing fees (2.9% +
                  $0.30).
                </p>

                <div className="middle-school-prep-card-fields">
                  <fieldset className="middle-school-prep-fieldset">
                    <legend>Select a program *</legend>
                    <div className="middle-school-prep-program-options">
                      {PROGRAM_OPTIONS.map((option) => (
                        <label
                          key={option.value}
                          className="middle-school-prep-radio"
                        >
                          <input
                            type="radio"
                            name="programOption"
                            value={option.value}
                            checked={paymentForm.programOption === option.value}
                            onChange={handlePaymentFieldChange}
                          />
                          {option.label} ({option.priceLabel})
                        </label>
                      ))}
                    </div>
                    {paymentErrors.programOption && (
                      <span className="registration-form-error">
                        {paymentErrors.programOption}
                      </span>
                    )}
                  </fieldset>

                  <label htmlFor="payStudentName">Student name *</label>
                  <input
                    type="text"
                    id="payStudentName"
                    name="studentName"
                    value={paymentForm.studentName}
                    onChange={handlePaymentFieldChange}
                    placeholder="Name of the student this tuition is for"
                  />
                  {paymentErrors.studentName && (
                    <span className="registration-form-error">
                      {paymentErrors.studentName}
                    </span>
                  )}

                  {isPaying && !stripeCheckout && (
                    <p className="middle-school-prep-stripe-loading">
                      Loading secure card form...
                    </p>
                  )}

                  {paymentLoadError && (
                    <span className="registration-form-error">
                      {paymentLoadError}
                    </span>
                  )}

                  {stripeCheckout && (
                    <MiddleSchoolPrepStripeCheckout
                      key={`${stripeCheckout.programOption}-${stripeCheckout.amountCents}`}
                      publishableKey={stripeCheckout.publishableKey}
                      amountCents={stripeCheckout.amountCents}
                      programOption={stripeCheckout.programOption}
                      amountDisplay={stripeCheckout.amountDisplay}
                      baseDisplay={stripeCheckout.baseDisplay}
                      feeDisplay={stripeCheckout.feeDisplay}
                      studentName={paymentForm.studentName}
                      onMissingStudentName={() =>
                        setPaymentErrors((prev) => ({
                          ...prev,
                          studentName: "Student name is required",
                        }))
                      }
                      onSuccess={handlePaymentSuccess}
                    />
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      )}

      {showDialog && (
        <div className="middle-school-prep-dialog-overlay">
          <div className="middle-school-prep-success-dialog">
            <p>{dialogMessage}</p>
            <button
              type="button"
              className="middle-school-prep-success-close"
              onClick={() => setShowDialog(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MiddleSchoolPrep;
